/* ==========================================================================
   pdp.js — product page: stage gallery controls, one-line title, size label,
   colours of the model («Кадр» marks and the stage preview)
   --------------------------------------------------------------------------
   The theme's own MediaGallery (product.js) still decides which slide is
   active: a thumbnail click or a variant change calls setActiveMedia(), which
   puts .is-active on the slide. This file only adds what the stage needs on
   top of that and never keeps a second source of truth:

     desktop  slides are stacked and cross-fade on .is-active (product.css);
              arrows, arrow keys and thumbnail hover call setActiveMedia()
     mobile   slides are a native scroll-snap strip; the slide nearest the
              left edge becomes .is-active as the strip scrolls

   In both cases the counter and the progress line are read back from
   .is-active. Without this file the gallery still works through the
   thumbnails and swipe; the counter just stays at 01.
   ========================================================================== */
(() => {
  const desktop = window.matchMedia('(min-width: 778px)');

  /* ---------- 1. Stage ---------- */
  class Stage {
    constructor(gallery) {
      this.gallery = gallery;
      this.track = gallery.querySelector('[id^="Slider-Gallery"]');
      this.meta = gallery.querySelector('[data-stage-meta]');
      if (!this.track || !this.meta) return;

      this.current = this.meta.querySelector('[data-stage-current]');
      this.thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');

      this.meta.querySelector('[data-stage-prev]').addEventListener('click', () => this.step(-1));
      this.meta.querySelector('[data-stage-next]').addEventListener('click', () => this.step(1));

      /* Thumbnail hover previews the picture on pointer devices; the theme
         already handles the click. */
      if (this.thumbs && window.matchMedia('(hover: hover)').matches) {
        this.thumbs.querySelectorAll('[data-target]').forEach((item) => {
          item.addEventListener('mouseenter', () => { if (desktop.matches) this.activate(item.dataset.target); });
        });
      }

      /* Mobile: the strip scrolls natively, the nearest slide takes .is-active. */
      let queued = false;
      this.track.addEventListener('scroll', () => {
        if (desktop.matches || queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this.followScroll(); });
      }, { passive: true });

      /* The theme moves .is-active (and may reorder slides on a variant
         change); the counter follows whatever it did. */
      new MutationObserver(() => this.sync()).observe(this.track, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });

      gallery.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      });

      this.sync();
    }

    slides() {
      return Array.from(this.track.querySelectorAll('.slider__slide')).filter((s) => getComputedStyle(s).display !== 'none');
    }

    index(slides = this.slides()) {
      const i = slides.findIndex((s) => s.classList.contains('is-active'));
      return i < 0 ? 0 : i;
    }

    activate(mediaId) {
      if (typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(mediaId, false);
    }

    step(delta) {
      const slides = this.slides();
      if (slides.length < 2) return;
      const next = slides[(this.index(slides) + delta + slides.length) % slides.length];
      if (desktop.matches) {
        this.activate(next.dataset.mediaId);
      } else {
        this.track.scrollTo({ left: next.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
    }

    followScroll() {
      const slides = this.slides();
      if (slides.length < 2) return;
      const left = this.track.scrollLeft + this.track.offsetLeft;
      let nearest = slides[0];
      slides.forEach((s) => { if (Math.abs(s.offsetLeft - left) < Math.abs(nearest.offsetLeft - left)) nearest = s; });
      if (nearest.classList.contains('is-active')) return;
      slides.forEach((s) => s.classList.toggle('is-active', s === nearest));   // observer → sync()
    }

    sync() {
      const slides = this.slides();
      const i = this.index(slides);
      const label = String(i + 1).padStart(2, '0');
      if (this.current && this.current.textContent !== label) this.current.textContent = label;
      this.gallery.style.setProperty('--stageIndex', i);
      this.gallery.style.setProperty('--stageTotal', Math.max(slides.length, 1));

      /* The theme only marks a thumbnail once something was clicked. */
      if (!this.thumbs || !slides[i]) return;
      const active = this.thumbs.querySelector(`[data-target="${slides[i].dataset.mediaId}"] button`);
      if (active && !active.hasAttribute('aria-current')) {
        this.thumbs.querySelectorAll('button[aria-current]').forEach((b) => b.removeAttribute('aria-current'));
        active.setAttribute('aria-current', 'true');
      }
    }
  }

  /* ---------- 2. Product name on one line while the column allows it ----------
     The size steps down from the stylesheet value to a floor; if the name
     still does not fit, it wraps at the stylesheet size instead. */
  const FLOOR = 21;   // px

  function fitTitle(title) {
    title.classList.remove('is-fit');
    title.style.fontSize = '';
    if (!desktop.matches) return;

    const top = parseFloat(getComputedStyle(title).fontSize);
    let size = top;
    title.classList.add('is-fit');
    while (title.scrollWidth > title.clientWidth && size > FLOOR) {
      size -= 0.5;
      title.style.fontSize = size + 'px';
    }
    if (title.scrollWidth > title.clientWidth) {
      title.classList.remove('is-fit');
      title.style.fontSize = '';
    }
  }

  function watchTitle(title) {
    let queued = false;
    const run = () => { queued = false; fitTitle(title); };
    /* Measured on the next frame: resizing the title inside the observer
       callback would re-trigger the observer. */
    new ResizeObserver(() => { if (!queued) { queued = true; requestAnimationFrame(run); } }).observe(title.parentElement);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    run();
  }

  /* ---------- 3. Selected size next to the option name ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('variant-selects input[type="radio"]');
    if (!input) return;
    const label = input.closest('fieldset')?.querySelector('[data-option-value]');
    if (label) label.textContent = input.value;
  });

  /* ---------- 4. Colours of the model: «Кадр» (snippets/flux-colors.liquid) ----------
     One colour is in focus: the current one at rest, the one under the
     pointer (or keyboard focus) while it is there. The corner marks live in
     the current tile and are only shifted onto the tile in focus, so with or
     without this script the page first paints them in the right place — the
     page transition (assets/wss.css, flux-mark) captures that paint.
     Desktop with hover also previews the colour on the stage; on click the
     preview hands its picture name to the page transition, so the photo the
     visitor sees is the one that travels into the next page. */
  const canPeek = window.matchMedia('(min-width: 778px) and (hover: hover)');

  class FluxColors extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.current = this.querySelector('.flux-colors__item.is-current');
      this.mark = this.querySelector('.flux-colors__mark');
      this.value = this.closest('.flux-colors')?.querySelector('[data-flux-value]');
      this.restValue = this.value ? this.value.textContent : '';

      this.addEventListener('pointerover', (e) => {
        const item = e.target.closest('.flux-colors__item');
        if (item && e.pointerType !== 'touch') this.focusOn(item);
      });
      this.addEventListener('pointerleave', () => this.focusOn(null));
      this.addEventListener('focusin', (e) => {
        const item = e.target.closest('.flux-colors__item');
        if (item) this.focusOn(item);
      });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.focusOn(null); });
      this.addEventListener('pointerenter', () => this.preload(), { once: true });
      this.addEventListener('click', (e) => this.leave(e));

      /* Back through the back/forward cache: the page comes back as it was
         left, mid-hover. */
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        if (this.peek) this.peek.querySelector('img').style.viewTransitionName = '';
        this.focusOn(null);
      });
    }

    focusOn(item) {
      const target = item || this.current;
      this.classList.toggle('is-hovering', !!item && item !== this.current);
      this.querySelectorAll('.flux-colors__item').forEach((i) => i.classList.toggle('is-target', i === target));

      if (this.mark && this.current && target) {
        const dx = target.offsetLeft - this.current.offsetLeft;
        const dy = target.offsetTop - this.current.offsetTop;
        this.mark.style.translate = dx || dy ? `${dx}px ${dy}px` : '';
      }
      if (this.value) this.value.textContent = (item && item.dataset.value) || this.restValue;
      this.showPeek(item && item !== this.current ? item : null);
    }

    stage() {
      if (this.peek !== undefined) return this.peek;
      this.peek = null;
      const viewer = document.querySelector('.product-page media-gallery[data-media-thumbnails="true"] > slider-component[id^="GalleryViewer"]');
      if (!viewer) return null;
      this.gallery = viewer.closest('media-gallery');
      this.peek = document.createElement('div');
      this.peek.className = 'stage-peek';
      this.peek.setAttribute('aria-hidden', 'true');
      this.peek.innerHTML = '<img alt=""><span class="stage-peek__name"></span>';
      viewer.appendChild(this.peek);
      return this.peek;
    }

    showPeek(item) {
      if (!this.hasAttribute('data-stage-preview') || !canPeek.matches) return;
      const peek = this.stage();
      if (!peek) return;
      const on = !!(item && item.dataset.peek);
      if (on) {
        const img = peek.querySelector('img');
        if (img.getAttribute('src') !== item.dataset.peek) img.src = item.dataset.peek;
        peek.querySelector('.stage-peek__name').textContent = item.getAttribute('title') || '';
        peek.dataset.href = item.getAttribute('href');
      }
      peek.classList.toggle('is-on', on);
      this.gallery.classList.toggle('is-peeking', on);
    }

    preload() {
      if (!this.hasAttribute('data-stage-preview') || !canPeek.matches) return;
      this.querySelectorAll('.flux-colors__item:not(.is-current)[data-peek]').forEach((i) => { new Image().src = i.dataset.peek; });
    }

    /* The picture on the stage travels into the next page (wss.js names the
       first slide of a product page pdp-media; here the preview takes the
       name instead while it is what the visitor is looking at). */
    leave(e) {
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const item = e.target.closest('.flux-colors__item');
      const peek = this.peek;
      if (!item || !peek || !peek.classList.contains('is-on') || peek.dataset.href !== item.getAttribute('href')) return;
      document.querySelectorAll('.product-page .slider__slide:first-child img').forEach((i) => { i.style.viewTransitionName = 'none'; });
      peek.querySelector('img').style.viewTransitionName = 'pdp-media';
    }
  }
  if (!customElements.get('flux-colors')) customElements.define('flux-colors', FluxColors);

  const init = () => {
    document.querySelectorAll('.product-page media-gallery[data-media-thumbnails="true"]').forEach((g) => new Stage(g));
    document.querySelectorAll('.product-page .title h1').forEach(watchTitle);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
